// src/mobile_pages/meeting/MeetingRoomManage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { meetingApi } from "../../api/meeting";
import { MoreVertical, Pencil, Trash2, Check, X } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none text-base placeholder-slate-400 focus:bg-white focus:border-slate-400 focus:ring-2 focus:ring-slate-900/5";

export default function MeetingRoomManage() {
  const { id: projectId } = useParams();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [menuOpenId, setMenuOpenId] = useState(null); // 더보기 메뉴 열린 항목 ID

  const fetchRooms = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await meetingApi.rooms.list({ project: projectId });
      const items = Array.isArray(data?.results) ? data.results : data;
      setRooms(items || []);
    } catch (e) {
      console.error(e);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [projectId]);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = () => setMenuOpenId(null);
    if (menuOpenId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [menuOpenId]);

  const addRoom = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await meetingApi.rooms.create({
        project: projectId,
        name,
        order: rooms.length + 1,
      });
      setNewName("");
      fetchRooms();
    } catch (e) {
      const data = e?.response?.data;
      let msg = "회의실 추가에 실패했습니다.";
      if (data?.name) msg = Array.isArray(data.name) ? data.name[0] : data.name;
      alert(msg);
    }
  };

  const updateRoom = async (room, patch) => {
    try {
      await meetingApi.rooms.update(room.id, patch);
      fetchRooms();
      setEditingId(null);
    } catch (e) {
      const data = e?.response?.data;
      let msg = "저장에 실패했습니다.";
      if (data?.name) msg = Array.isArray(data.name) ? data.name[0] : data.name;
      alert(msg);
    }
  };

  const removeRoom = async (room) => {
    if (!window.confirm(`'${room.name}' 회의실을 삭제할까요?`)) return;
    try {
      await meetingApi.rooms.remove(room.id);
      fetchRooms();
    } catch (e) {
      alert("삭제에 실패했습니다.");
    }
  };

  const toggleActive = (room) => {
    updateRoom(room, { is_active: !room.is_active });
  };

  const startEdit = (room) => {
    setEditingId(room.id);
    setEditValue(room.name);
    setMenuOpenId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = (room) => {
    if (!editValue.trim()) {
      alert("회의실명을 입력해주세요.");
      return;
    }
    updateRoom(room, { name: editValue.trim() });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 본문 영역 */}
      <main className="mx-auto max-w-md px-4 pt-4 pb-8">
        {/* 페이지 타이틀 */}
        <header className="mb-6 pt-2">
          <h1 className="text-[28px] font-bold tracking-tight text-slate-900">
            회의실 관리
          </h1>
          <p className="mt-1 text-[15px] text-slate-500">
            자주 사용하는 회의실을 등록하고 관리하세요.
          </p>
        </header>

        {/* 새 회의실 추가 */}
        <section className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <label className="text-gray-medium mb-2 block">새 회의실 추가</label>
          <div className="flex gap-2">
            <input
              className={inputCls + " flex-1"}
              placeholder="예) 2층 소회의실"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addRoom()}
            />
            <button
              type="button"
              className="btn-save h-11 px-5 rounded-xl whitespace-nowrap"
              onClick={addRoom}
            >
              추가
            </button>
          </div>
        </section>

        {/* 회의실 목록 */}
        <section className="space-y-3">
          {loading ? (
            <div className="text-center text-muted py-8">불러오는 중...</div>
          ) : rooms.length === 0 ? (
            <div className="text-center text-muted py-8 rounded-2xl border border-gray-200 bg-white">
              등록된 회의실이 없습니다.
            </div>
          ) : (
            rooms.map((room, index) => (
              <div
                key={room.id}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                {editingId === room.id ? (
                  /* 수정 모드 */
                  <div className="space-y-3">
                    <input
                      className={inputCls}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(room);
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        className="btn-save flex-1 h-10 rounded-xl flex items-center justify-center gap-1"
                        onClick={() => saveEdit(room)}
                      >
                        <Check size={16} />
                        저장
                      </button>
                      <button
                        className="btn-cancel flex-1 h-10 rounded-xl flex items-center justify-center gap-1"
                        onClick={cancelEdit}
                      >
                        <X size={16} />
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  /* 일반 모드 */
                  <div className="flex items-center justify-between">
                    {/* 왼쪽: 번호 + 이름 */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="text-muted text-xs w-6 text-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-body font-medium truncate">
                        {room.name}
                      </span>
                    </div>

                    {/* 오른쪽: 사용여부 토글 + 더보기 메뉴 */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* 사용 여부 토글 */}
                      <button
                        type="button"
                        onClick={() => toggleActive(room)}
                        className={`relative w-12 h-7 rounded-full transition-colors ${
                          room.is_active ? "bg-blue-500" : "bg-gray-300"
                        }`}
                        title={room.is_active ? "사용중" : "미사용"}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-all duration-200 ${
                            room.is_active ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>

                      {/* 더보기 메뉴 */}
                      <div className="relative">
                        <button
                          type="button"
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenId(menuOpenId === room.id ? null : room.id);
                          }}
                        >
                          <MoreVertical size={18} className="text-gray-500" />
                        </button>

                        {/* 드롭다운 메뉴 */}
                        {menuOpenId === room.id && (
                          <div className="absolute right-0 top-10 z-10 w-32 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <button
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              onClick={() => startEdit(room)}
                            >
                              <Pencil size={14} />
                              수정
                            </button>
                            <button
                              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              onClick={() => {
                                setMenuOpenId(null);
                                removeRoom(room);
                              }}
                            >
                              <Trash2 size={14} />
                              삭제
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </section>
      </main>
    </div>
  );
}
